import { useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UploadDocumentModal = ({ open, onClose, token, entityId, entityType, onUploadComplete }) => {
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadForm, setUploadForm] = useState({
        name: '',
        type: 'other',
        description: ''
    });

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                toast.error('El archivo no puede superar los 10MB');
                return;
            }
            setSelectedFile(file);
            // Auto-fill name if empty
            if (!uploadForm.name) {
                const nameWithoutExt = file.name.split('.').slice(0, -1).join('.') || file.name;
                setUploadForm(prev => ({ ...prev, name: nameWithoutExt }));
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.error('Selecciona un archivo');
            return;
        }
        if (!uploadForm.name.trim()) {
            toast.error('El nombre es requerido');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('entity_type', entityType);
            formData.append('entity_id', entityId);
            formData.append('document_type', uploadForm.type);
            formData.append('name', uploadForm.name);
            if (uploadForm.description) {
                formData.append('description', uploadForm.description);
            }

            await axios.post(`${API}/documents`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success('Documento subido correctamente');
            if (onUploadComplete) onUploadComplete();
            handleClose();
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.error('Error al subir documento');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setUploadForm({
            name: '',
            type: 'other',
            description: ''
        });
        onClose();
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const k = 1024;
        const sized = bytes / k;
        if (sized < 1024) return `${sized.toFixed(2)} KB`;
        return `${(sized / 1024).toFixed(2)} MB`;
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Subir Documento</DialogTitle>
                    <DialogDescription>
                        Sube archivos PDF, imágenes o documentos de texto. Máximo 10MB.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* File Input */}
                    <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="file">Archivo</Label>
                        <Input id="file" type="file" onChange={handleFileSelect} />
                        {selectedFile && (
                            <p className="text-xs text-gray-500">
                                Seleccionado: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                            </p>
                        )}
                    </div>

                    {/* Name */}
                    <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="name">Nombre visible</Label>
                        <Input
                            id="name"
                            value={uploadForm.name}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Ej. Contrato de Servicios 2024"
                        />
                    </div>

                    {/* Type */}
                    <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="type">Tipo de documento</Label>
                        <Select
                            value={uploadForm.type}
                            onValueChange={(val) => setUploadForm(prev => ({ ...prev, type: val }))}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="contract">Contrato</SelectItem>
                                <SelectItem value="power">Poder</SelectItem>
                                <SelectItem value="invoice">Factura</SelectItem>
                                <SelectItem value="correspondence">Correspondencia</SelectItem>
                                <SelectItem value="legal">Legal</SelectItem>
                                <SelectItem value="other">Otro</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Description */}
                    <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="description">Descripción (opcional)</Label>
                        <Textarea
                            id="description"
                            value={uploadForm.description}
                            onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Notas adicionales sobre el archivo..."
                            rows={2}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Cancelar
                    </Button>
                    <Button onClick={handleUpload} disabled={uploading || !selectedFile || !uploadForm.name}>
                        {uploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Subiendo...
                            </>
                        ) : (
                            'Subir Archivo'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
